import { useState, useEffect, useCallback } from "react";
import { List, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  containerSelector?: string;
  headingSelector?: string;
  offset?: number;
}

export const TableOfContents = ({
  containerSelector = "main",
  headingSelector = "h2, h3",
  offset = 100,
}: TableOfContentsProps) => {
  const [headings, setHeadings] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract headings from the page
  useEffect(() => {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const elements = container.querySelectorAll(headingSelector);
    const items: TocItem[] = [];

    elements.forEach((el, index) => {
      // Generate ID if not present
      if (!el.id) {
        el.id = `toc-heading-${index}`;
      }

      const text = el.textContent?.trim() || "";
      if (text) {
        items.push({
          id: el.id,
          text: text.length > 50 ? text.substring(0, 50) + "..." : text,
          level: parseInt(el.tagName.charAt(1)),
        });
      }
    });

    setHeadings(items);
  }, [containerSelector, headingSelector]);

  // Track scroll position to highlight active section
  const handleScroll = useCallback(() => {
    if (headings.length === 0) return;

    const scrollPosition = window.scrollY + offset + 20;

    // Find the current active heading
    let currentActive = headings[0]?.id || "";

    for (const heading of headings) {
      const element = document.getElementById(heading.id);
      if (element) {
        const elementTop = element.offsetTop;
        if (scrollPosition >= elementTop) {
          currentActive = heading.id;
        }
      }
    }

    setActiveId(currentActive);
  }, [headings, offset]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Handle click - scroll to element from top
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();

    const element = document.getElementById(id);
    if (!element) return;

    // Get the element's position from the TOP of the document
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = window.pageYOffset + elementPosition - offset;

    // Scroll to the calculated position
    window.scrollTo({
      top: Math.max(0, offsetPosition),
      behavior: "smooth",
    });

    setActiveId(id);
    setIsExpanded(false); // Collapse on mobile after click
  };

  if (headings.length < 3) {
    return null; // Don't show TOC for pages with few headings
  }

  return (
    <>
      {/* Mobile toggle button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="lg:hidden fixed bottom-20 right-4 z-40 bg-primary text-primary-foreground p-3 rounded-full shadow-lg hover:bg-primary/90 transition-colors"
        aria-label="Table of contents"
      >
        <List className="w-5 h-5" />
      </button>

      {/* Mobile overlay */}
      {isExpanded && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* TOC Panel */}
      <nav
        className={cn(
          "fixed z-50 bg-card border border-border rounded-xl shadow-lg transition-all duration-300",
          // Mobile: slide up from bottom
          "lg:hidden bottom-0 left-0 right-0 max-h-[60vh] overflow-y-auto",
          isExpanded ? "translate-y-0" : "translate-y-full",
          // Desktop: sidebar on right
          "lg:block lg:translate-y-0 lg:right-8 lg:left-auto lg:bottom-auto lg:top-28 lg:w-64 lg:max-h-[calc(100vh-150px)]"
        )}
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
            <List className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">
              On this page
            </span>
          </div>

          <ul className="space-y-1">
            {headings.map((heading) => (
              <li key={heading.id}>
                <a
                  href={`#${heading.id}`}
                  onClick={(e) => handleClick(e, heading.id)}
                  className={cn(
                    "flex items-start gap-1.5 py-1.5 text-sm transition-colors rounded-md px-2 -mx-2",
                    heading.level === 3 && "pl-4",
                    activeId === heading.id
                      ? "text-primary bg-primary/5 font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  <ChevronRight
                    className={cn(
                      "w-3 h-3 mt-1 shrink-0 transition-transform",
                      activeId === heading.id ? "text-primary" : "text-muted-foreground/50"
                    )}
                  />
                  <span className="leading-tight">{heading.text}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Desktop sticky TOC */}
      <nav
        className={cn(
          "hidden lg:block fixed right-8 top-28 w-56 max-h-[calc(100vh-150px)] overflow-y-auto",
          "bg-card/80 backdrop-blur-sm border border-border rounded-xl shadow-sm"
        )}
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
            <List className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">
              On this page
            </span>
          </div>

          <ul className="space-y-0.5">
            {headings.map((heading) => (
              <li key={heading.id}>
                <a
                  href={`#${heading.id}`}
                  onClick={(e) => handleClick(e, heading.id)}
                  className={cn(
                    "flex items-start gap-1.5 py-1.5 text-xs transition-all rounded-md px-2 -mx-2",
                    heading.level === 3 && "pl-4",
                    activeId === heading.id
                      ? "text-primary bg-primary/10 font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  <ChevronRight
                    className={cn(
                      "w-3 h-3 mt-0.5 shrink-0 transition-transform",
                      activeId === heading.id ? "text-primary rotate-90" : "text-muted-foreground/40"
                    )}
                  />
                  <span className="leading-tight">{heading.text}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </>
  );
};
